'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { MapPin, Calendar, Search, ChevronDown } from 'lucide-react';

export default function HeroSection() {
  const router = useRouter();
  const [pickupDate, setPickupDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [category, setCategory] = useState('');

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (category) params?.set('category', category);
    if (pickupDate) params?.set('pickup', pickupDate);
    if (returnDate) params?.set('return', returnDate);
    router?.push(`/our-cars-gallery?${params?.toString()}`);
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-foreground">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920)' }}
      />
      <div className="gradient-hero absolute inset-0" />
      {/* Content */}
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 pt-28 pb-24 w-full">
        <div className="max-w-2xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-primary" />
            <span className="text-primary text-xs font-bold uppercase tracking-widest">Oslo, Norway</span>
          </div>

          {/* Headline */}
          <h1 className="text-white font-black leading-none mb-6" style={{ fontWeight: 900, fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
            Drive Norway<br />
            <span className="text-primary">Your Way.</span>
          </h1>
          <p className="text-white/65 text-lg leading-relaxed mb-10 max-w-lg">
            30 vehicles from Economy to SUV — transparent NOK pricing, free cancellation, no hidden fees. Pickup at Munkebekken 400, Oslo.
          </p>
        </div>

        {/* Booking bar */}
        <div className="bg-white shadow-modal max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {/* Category */}
            <div className="relative p-4">
              <label className="label-text text-muted-foreground block mb-1">
                <MapPin size={12} className="inline mr-1 mb-0.5" />Category
              </label>
              <div className="relative">
                <select
                  value={category}
                  onChange={(e) => setCategory(e?.target?.value)}
                  className="w-full appearance-none bg-transparent font-700 text-foreground text-base pr-6 outline-none cursor-pointer"
                >
                  <option value="">All Categories</option>
                  <option value="Economy">Economy — from 450 NOK</option>
                  <option value="Comfort">Comfort — from 750 NOK</option>
                  <option value="SUV">SUV — from 850 NOK</option>
                </select>
                <ChevronDown size={14} className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Pickup date */}
            <div className="p-4">
              <label className="label-text text-muted-foreground block mb-1">
                <Calendar size={12} className="inline mr-1 mb-0.5" />Pickup Date
              </label>
              <input
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e?.target?.value)}
                className="w-full bg-transparent font-700 text-foreground text-base outline-none"
              />
            </div>

            {/* Return date */}
            <div className="p-4">
              <label className="label-text text-muted-foreground block mb-1">
                <Calendar size={12} className="inline mr-1 mb-0.5" />Return Date
              </label>
              <input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e?.target?.value)}
                className="w-full bg-transparent font-700 text-foreground text-base outline-none"
              />
            </div>
          </div>

          {/* Search button */}
          <div className="p-4 border-t border-border bg-muted/50 flex items-center justify-between">
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>✓ Free cancellation</span>
              <span>✓ No hidden fees</span>
              <span className="hidden sm:inline">✓ 10% off 7+ days</span>
            </div>
            <button onClick={handleSearch} className="btn-primary gap-2">
              <Search size={16} />
              Search Cars
            </button>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
        <span className="text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-px h-8 bg-white/20 animate-pulse" />
      </div>
    </section>
  );
}